(function () {
  'use strict';

  const translated = (options, key, fallback, values = {}) => options.translate?.(key, values) || fallback;
  const clean = value => String(value || '').trim().replace(/\s+/g, ' ');
  const semanticStatus = (value, options) => options.provider === 'Trax' ? clean(value).replace(/^Shipment\s*-\s*/i, '') : clean(value);
  const cityName = value => clean(value).toLowerCase().split(' ').map(word => {
    if (['tcp', 'dha', 'd.h.a'].includes(word)) return word.toUpperCase();
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
  const sentence = value => {
    const text = clean(value).toLowerCase();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
  };

  function validTimestamp(value) {
    const match = clean(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return false;
    const [year, month, day, hour, minute, second] = match.slice(1).map(value => Number(value || 0));
    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day && date.getUTCHours() === hour && date.getUTCMinutes() === minute && date.getUTCSeconds() === second;
  }

  function formatDate(value, options = {}) {
    const match = clean(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/);
    if (!match || !validTimestamp(value)) {
      const unavailable = translated(options, 'date_unavailable', 'Date unavailable');
      return { date: unavailable, time: '', full: unavailable, iso: '' };
    }
    const [, year, month, day, hour, minute] = match;
    const timestamp = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)));
    const locale = options.locale || 'en-GB';
    const date = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' }).format(timestamp);
    const time = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', hour: 'numeric', minute: '2-digit', hour12: true }).format(timestamp);
    return { date, time, full: `${date} · ${time}`, iso: clean(value).replace(' ', 'T') };
  }

  function eventPresentation(event, options = {}) {
    const raw = clean(event.status);
    if (options.provider === 'Prime') {
      return { title: raw || translated(options, 'event_courier_update', 'Courier update'), raw,
        reason: clean(event.reason), datetime: event.datetime, ...formatDate(event.datetime, options) };
    }
    const semantic = semanticStatus(raw, options);
    const isTrax = options.provider === 'Trax';
    let title = raw || translated(options, 'event_courier_update', 'Courier update');
    let match;
    if (/^shipment picked in$|^picked up$|^collected$/i.test(semantic) || (isTrax && /^rider picked$/i.test(semantic))) title = translated(options, 'event_collected', 'Collected by courier');
    else if (isTrax && /^booked$/i.test(semantic)) title = translated(options, 'status_booked', 'Booked');
    else if (isTrax && /^arrived at origin$/i.test(semantic)) title = translated(options, 'event_arrived_origin', 'Arrived at origin facility');
    else if (isTrax && /^arrived at destination$/i.test(semantic)) title = translated(options, 'event_arrived_destination', 'Arrived at destination facility');
    else if ((match = semantic.match(/^dispatched to (.+)$/i))) title = translated(options, 'event_dispatched_to', `Dispatched to ${cityName(match[1])}`, { city: cityName(match[1]) });
    else if ((match = semantic.match(/^arrived at station in (.+)$/i))) title = translated(options, 'event_arrived_at', `Arrived at ${cityName(match[1])}`, { city: cityName(match[1]) });
    else if ((match = semantic.match(/^assigned to courier in (.+)$/i))) title = translated(options, 'event_assigned_in', `Assigned to courier in ${cityName(match[1])}`, { city: cityName(match[1]) });
    else if (/^ready for return\b/i.test(semantic)) title = translated(options, 'event_return_marked', 'Marked for return');
    else if (/^pending$/i.test(semantic)) title = translated(options, 'event_pending', 'Delivery pending');
    else if (/^return to origin$/i.test(semantic)) title = translated(options, 'event_return_origin', 'Return to origin');
    else if (/^out for delivery$/i.test(semantic)) title = translated(options, 'event_out_for_delivery', 'Out for delivery');
    else if (/^delivered$/i.test(semantic)) title = translated(options, 'event_delivered', 'Delivered');
    const reason = clean(event.reason) || (semantic.match(/^ready for return\s*-\s*(.+)$/i)?.[1] || '');
    return { title, raw, reason: reason ? sentence(reason) : '', datetime: event.datetime, ...formatDate(event.datetime, options) };
  }

  function normalizeShipment(record, options = {}) {
    const isTrax = [record.partner, record.provider].some(value => clean(value).toLowerCase() === 'trax');
    const isPrime = [record.partner, record.provider].some(value => clean(value).toLowerCase() === 'prime');
    const presentationOptions = { ...options, provider: isPrime ? 'Prime' : isTrax ? 'Trax' : '' };
    const chronological = (record.events || []).map(event => ({ ...event })).sort((a, b) => Number(validTimestamp(a.datetime)) - Number(validTimestamp(b.datetime)) || clean(a.datetime).localeCompare(clean(b.datetime)));
    const latest = chronological.filter(event => validTimestamp(event.datetime)).pop();
    const sourceStatus = clean(latest?.status || record.raw_status);
    const status = semanticStatus(sourceStatus, presentationOptions).toLowerCase();
    let state = 'unknown';
    let title = sourceStatus || translated(options, 'status_awaiting', 'Awaiting a courier update');
    let description = translated(options, 'description_unknown', 'Check the tracking history for the courier’s latest update.');

    if (isPrime) {
      // Prime source labels are shown directly until their status semantics are verified.
    } else if (/^(being return|ready for return|return to origin|return in progress)\b/.test(status)) {
      state = 'return';
      title = translated(options, 'status_returning', 'Returning to sender');
      description = translated(options, 'description_returning', 'The courier has marked this parcel for return. Contact us if this is unexpected.');
    } else if (/^(returned to sender|returned to shipper|return received)$/.test(status)) {
      state = 'return';
      title = translated(options, 'status_returned', 'Returned to sender');
      description = translated(options, 'description_returned', 'The courier has confirmed the return. Contact us for help with your order.');
    } else if (/^(delivered|delivered successfully)$/.test(status)) {
      state = 'delivered';
      title = translated(options, 'status_delivered', 'Delivered');
      description = translated(options, 'description_delivered', 'The courier has marked your parcel as delivered.');
    } else if (/^(pending|undelivered|delivery failed|delivery attempted|not delivered)\b/.test(status)) {
      state = 'issue';
      title = translated(options, 'status_issue', 'Delivery issue');
      description = translated(options, 'description_issue', 'The courier has reported a delivery issue. Contact us if you need help.');
    } else if (/^(assigned to courier|assign to courier|out for delivery)\b/.test(status)) {
      state = 'out_for_delivery';
      title = translated(options, 'status_out_for_delivery', 'Out for delivery');
      description = record.destination_city ? translated(options, 'description_out_for_delivery_city', `Your parcel has been assigned to the delivery courier in ${cityName(record.destination_city)}.`, { city: cityName(record.destination_city) }) : translated(options, 'description_out_for_delivery', 'Your parcel has been assigned to the delivery courier.');
    } else if ((isTrax && status === 'arrived at destination') || /^arrived at station\b/.test(status)) {
      const atDestination = (isTrax && status === 'arrived at destination') || (clean(record.destination_city) && status.includes(clean(record.destination_city).toLowerCase()));
      state = atDestination ? 'at_destination' : 'transit';
      title = atDestination ? translated(options, 'status_at_destination', 'At destination facility') : translated(options, 'status_transit', 'In transit');
      if (atDestination) {
        description = clean(record.destination_city) ? translated(options, 'description_at_destination', `Your parcel has reached a delivery station in ${cityName(record.destination_city)}.`, { city: cityName(record.destination_city) }) : translated(options, 'description_at_destination_no_city', 'Your parcel has reached the destination delivery facility.');
      } else description = translated(options, 'description_transit', 'Your parcel is moving through the courier’s delivery network.');
    } else if ((isTrax && status === 'arrived at origin') || /^(dispatched|in transit|departed)\b/.test(status)) {
      state = 'transit';
      title = translated(options, 'status_transit', 'In transit');
      description = translated(options, 'description_transit', 'Your parcel is moving through the courier’s delivery network.');
    } else if ((isTrax && status === 'rider picked') || /^(shipment picked|picked up|collected)\b/.test(status)) {
      state = 'booked';
      title = translated(options, 'status_collected', 'Collected');
      description = translated(options, 'description_collected', 'The courier has recorded pickup. The next update will appear after your parcel moves through its network.');
    } else if (/^(booked|shipment created|label created)\b/.test(status)) {
      state = 'booked';
      title = translated(options, 'status_booked', 'Booked');
      description = translated(options, 'description_booked', 'Your shipment is booked. Tracking will update when the courier scans your parcel.');
    }

    return {
      state, title, description,
      tracking: record.tracking,
      provider: record.provider || '',
      partner: record.partner || '',
      rawStatus: clean(record.raw_status),
      latest: formatDate(latest?.datetime, options),
      events: chronological.reverse().map(event => eventPresentation(event, presentationOptions))
    };
  }

  const api = { normalizeShipment, formatDate, eventPresentation };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.TTTrackingModel = api;
})();
